import type { UploadedFile } from '@/types/chat'

/**
 * Live status row from /api/files/upload-status/{id} — the files table is
 * the durable source of truth for upload state.
 */
export interface LiveUploadStatus {
  status?: UploadedFile['status']
  errorMessage?: string
  failureReason?: string
  failure_reason?: string
  ai_file_id?: string
}

/** null = no row exists (404): the upload never completed. */
export type FetchUploadStatus = (fileId: string) => Promise<LiveUploadStatus | null>

const TRANSIENT: ReadonlySet<UploadedFile['status']> = new Set(['processing'])

/**
 * Re-derive transient upload statuses live on session hydrate.
 *
 * Session blobs snapshot UI state, freezing 'processing' for files that
 * finished (or died) after the last save — the eternal-spinner pills.
 *
 * Identity (2026-06-11 regression lesson): UploadedFile.id is a CLIENT
 * handle minted at drop time; the DB row id lives in file_id once
 * complete-upload returns. Verification must use the backend id —
 * polling the client handle 404s for every file and produced false
 * "Upload never completed" errors on files that were ready.
 *
 * Rules:
 *  - transient + file_id: poll the backend id. Live row wins; a missing
 *    row (404) now genuinely means the upload never completed.
 *  - transient + no file_id: the snapshot predates upload completion and
 *    cannot be verified — DROP the pill (returning null) rather than lie
 *    in either direction. A file that did complete is still in the
 *    saved-files list.
 *  - terminal statuses are trusted as-is; failed fetches keep the
 *    snapshot so the next hydrate retries.
 */
export async function reconcileUploadStatuses(
  files: UploadedFile[],
  fetchStatus: FetchUploadStatus
): Promise<UploadedFile[]> {
  const out = await Promise.all(
    files.map(async (f): Promise<UploadedFile | null> => {
      const backendId = (f as { file_id?: string }).file_id
      // Poisoned-snapshot guard: between the false-error regression and its
      // hotfix, autosaves persisted bogus 'error' pills with no backend id
      // into session blobs. error is terminal, so trusting it would keep
      // the poison forever. An error WITHOUT a backend id is exactly as
      // unverifiable as a processing pill without one — drop it. True
      // extraction errors always carry a file_id (their row existed).
      if (f.status === 'error' && !backendId) return null
      if (!TRANSIENT.has(f.status)) return f
      if (!backendId) return null
      try {
        const live = await fetchStatus(backendId)
        if (live === null) {
          return {
            ...f,
            status: 'error' as const,
            errorMessage: 'Upload never completed — please re-upload this file.',
          }
        }
        const failureReason = live.failureReason ?? live.failure_reason
        return {
          ...f,
          ...(live.status ? { status: live.status } : {}),
          ...(live.errorMessage ? { errorMessage: live.errorMessage } : {}),
          ...(failureReason ? { failureReason } : {}),
          ...(live.ai_file_id ? { ai_file_id: live.ai_file_id } : {}),
        }
      } catch {
        // Transient fetch failure — keep the snapshot; next hydrate retries.
        return f
      }
    })
  )
  return out.filter((f): f is UploadedFile => f !== null)
}
